import { logger } from '../services/logger.ts';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { Search, ArrowUpRight, ArrowDownRight, Clock, AlertTriangle, Percent, X, Download, ArrowLeft, Gift, Briefcase } from 'lucide-react';
import ExcelJS from 'exceljs';
import { formatDateWithTimezone } from '../utils/dateUtils.ts';
import Select from 'react-select';
import { ClickableUserName } from '../components/common/ClickableUserName';

interface Transaction {
  id: string;
  created_at: string;
  user_id: string;
  type: 'deposit' | 'withdrawal' | 'bet_placement' | 'bet_win' | 'bet_loss' | 'bet_cancelled' | 'service_fee' | 'payout' | 'platform_liquidity_return' | 'wayja_credit' | 'service_fee_cancelled' | 'affiliate_commission' | 'creator_commission';
  amount: number;
  balance_after: number;
  description: string | null;
  status: 'pending' | 'completed' | 'failed';
  user_email?: string | null;
  user_full_name?: string;
  user_first_name?: string;
  user_last_name?: string;
  related_entity_id?: string;
  related_entity_type?: 'bet' | 'bet_payout' | 'deposit' | 'withdrawal' | 'market';
  completed_at?: string;
  market_id?: string;
  market_title?: string;
}

interface MarketOption {
  market_id: string;
  market_title: string;
  transaction_count: number;
}

// Helper function to generate page numbers for pagination
const generatePageNumbers = (currentPage: number, totalPages: number, maxVisiblePages = 5): (number | string)[] => {
  if (totalPages <= maxVisiblePages) {
    // If total pages is small, show all page numbers
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisiblePages / 2);
  
  // Always add the first page
  pages.push(1);

  let startPage = Math.max(2, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  // Adjust if near the start
  if (currentPage - halfVisible <= 2) {
      endPage = Math.min(totalPages - 1, maxVisiblePages - 1); // Account for first and last page
  }
  
  // Adjust if near the end
  if (currentPage + halfVisible >= totalPages - 1) {
      startPage = Math.max(2, totalPages - maxVisiblePages + 2); // Account for first and last page
  }

  // Add ellipsis before the main range if needed
  if (startPage > 2) {
    pages.push('...');
  }

  // Add the main range of pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis after the main range if needed
  if (endPage < totalPages - 1) {
    pages.push('...');
  }

  // Always add the last page
  pages.push(totalPages);

  return pages;
};

export function AdminTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputSearchTerm, setInputSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [marketFilter, setMarketFilter] = useState<{ value: string; label: string } | null>({ value: 'all', label: 'All Markets' });
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();

  // Fetch markets for the filter
  useEffect(() => {
    const fetchMarkets = async () => {
      if (!user || user.app_metadata?.role !== 'admin') {
        return;
      }

      try {
        setLoadingMarkets(true);
        const { data, error } = await supabase.rpc('get_market_filter_options');
        
        if (error) throw error;
        
        setMarketOptions(data || []);
      } catch (err) {
        logger.error('Error fetching market options:', err);
      } finally {
        setLoadingMarkets(false);
      }
    };

    fetchMarkets();
  }, [user]);

  // Set up debounced search
  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputSearchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timer);
    };
  }, [inputSearchTerm]);

  // Apply debounced search
  useEffect(() => {
    const cleanup = debouncedSearch();
    return cleanup;
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is admin
        if (!user || user.app_metadata?.role !== 'admin') {
          navigate('/markets');
          return;
        }

        setLoading(true);
        
        // Get transaction type filter (null if 'all')
        const typeFilter = filterType !== 'all' ? filterType : null;
        
        // Get status filter (null if 'all')
        const statusFilter = filterStatus !== 'all' ? filterStatus : null;
        
        // Get search filter (null if empty)
        const searchFilter = searchTerm ? searchTerm : null;

        // Get market filter (null if 'all')
        const marketIdFilter = marketFilter && marketFilter.value !== 'all' ? marketFilter.value : null;

        // Prepare date filters
        let startDateFilter: string | null = null;
        let endDateFilter: string | null = null;
        
        if (dateFilter !== 'all') {
          const now = new Date();
          const endOfDay = new Date(now);
          endOfDay.setHours(23, 59, 59, 999);
          
          if (dateFilter === 'custom' && startDate) {
            // Use custom date range
            startDateFilter = new Date(startDate).toISOString();
            
            if (endDate) {
              const customEndDate = new Date(endDate);
              customEndDate.setHours(23, 59, 59, 999);
              endDateFilter = customEndDate.toISOString();
            } else {
              // If only start date is specified, use current date as end date
              endDateFilter = endOfDay.toISOString();
            }
          } else {
            // Use predefined date ranges
            endDateFilter = endOfDay.toISOString();
            
            const startOfDay = new Date(now);
            startOfDay.setHours(0, 0, 0, 0);
            
            if (dateFilter === 'today') {
              startDateFilter = startOfDay.toISOString();
            } else if (dateFilter === 'yesterday') {
              const yesterday = new Date(startOfDay);
              yesterday.setDate(yesterday.getDate() - 1);
              startDateFilter = yesterday.toISOString();
            } else if (dateFilter === 'week') {
              const lastWeek = new Date(startOfDay);
              lastWeek.setDate(lastWeek.getDate() - 7);
              startDateFilter = lastWeek.toISOString();
            } else if (dateFilter === 'month') {
              const lastMonth = new Date(startOfDay);
              lastMonth.setMonth(lastMonth.getMonth() - 1);
              startDateFilter = lastMonth.toISOString();
            } else if (dateFilter === 'year') {
              const lastYear = new Date(startOfDay);
              lastYear.setFullYear(lastYear.getFullYear() - 1);
              startDateFilter = lastYear.toISOString();
            }
          }
        }
        
        // Get count for pagination
        const { data: countData, error: countError } = await supabase.rpc(
          'get_admin_wallet_transactions_count',
          {
            p_type: typeFilter,
            p_status: statusFilter,
            p_search: searchFilter,
            p_start_date: startDateFilter,
            p_end_date: endDateFilter,
            p_market_id: marketIdFilter
          }
        );
        
        if (countError) throw countError;
        
        // Calculate total pages
        setTotalPages(Math.max(1, Math.ceil(countData / pageSize)));
        
        // Get transactions with user data
        const { data: transactionsData, error: transactionsError } = await supabase.rpc(
          'get_admin_wallet_transactions',
          {
            p_page_size: pageSize,
            p_page: page,
            p_type: typeFilter,
            p_status: statusFilter,
            p_search: searchFilter,
            p_start_date: startDateFilter,
            p_end_date: endDateFilter,
            p_market_id: marketIdFilter
          }
        );

        if (transactionsError) throw transactionsError;

        setTransactions(transactionsData || []);
        setError(null);
      } catch (err) {
        logger.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data. Please ensure you have the correct permissions.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, searchTerm, filterType, filterStatus, dateFilter, startDate, endDate, marketFilter, user, navigate, pageSize]);

  const formatDate = (dateString: string) => {
    return formatDateWithTimezone(dateString);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="w-4 h-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'bet_placement':
        return <ArrowUpRight className="w-4 h-4 text-orange-500" />;
      case 'service_fee':
        return <Percent className="w-4 h-4 text-orange-500" />;
      case 'bet_win':
      case 'payout':
      case 'platform_liquidity_return':
        return <ArrowDownRight className="w-4 h-4 text-green-500" />;
      case 'bet_loss':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'bet_cancelled':
      case 'service_fee_cancelled':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'affiliate_commission':
        return <Gift className="w-4 h-4 text-purple-500" />;
      case 'creator_commission':
        return <Briefcase className="w-4 h-4 text-pink-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTransactionDisplayType = (type: string) => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'withdrawal': return 'Withdrawal';
      case 'bet_placement': return 'Trade Placement';
      case 'bet_win': return 'Trade Win';
      case 'bet_loss': return 'Trade Loss';
      case 'bet_cancelled': return 'Trade Cancelled';
      case 'wayja_credit': return 'Wayja Credit';
      case 'payout': return 'Payout';
      case 'platform_liquidity_return': return 'Collateral return';
      case 'service_fee': return 'Service Fee';
      case 'service_fee_cancelled': return 'Fee Refund';
      case 'affiliate_commission': return 'Affiliate Commission';
      case 'creator_commission': return 'Creator Commission';
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isPositiveTransaction = (type: string, amount: number) => {
    // If the amount is positive, it's a credit (should show as positive/green)
    // If the amount is negative, it's a debit (should show as negative/red)
    if (amount > 0) return true;
    if (amount < 0) return false;
    
    // For zero amounts, use type-based logic
    const positiveTypes = ['deposit', 'bet_win', 'bet_cancelled', 'market_cancellation_refund', 'payout', 'platform_liquidity_return', 'wayja_credit', 'service_fee_cancelled', 'affiliate_commission', 'creator_commission'];
    return positiveTypes.includes(type);
  };
  
  const getRelatedEntityInfo = (transaction: Transaction) => {
    if (transaction.related_entity_type && transaction.related_entity_id) {
      switch (transaction.related_entity_type) {
        case 'bet':
        case 'bet_payout':
        case 'market':
          return <Link to={`/markets/${transaction.market_id || transaction.related_entity_id}`} className="text-sm text-blue-600 hover:underline">{transaction.market_title || 'View Market'}</Link>;
        case 'deposit':
          return <span className="text-sm text-gray-500">Deposit Transaction</span>;
        case 'withdrawal':
          return <span className="text-sm text-gray-500">Withdrawal Request</span>;
        default:
          return <span className="text-sm text-gray-500">{transaction.related_entity_type}</span>;
      }
    }
    return null;
  };
  
  const openTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
  };

  const closeTransactionDetails = () => {
    setSelectedTransaction(null);
  };
  
  // New user display logic
  const formatUserDisplay = (transaction: Transaction) => {
    // Check if this is the Wayja admin wallet (all zeros UUID)
    if (transaction.user_id === '00000000-0000-0000-0000-000000000000') {
      return (
        <span className="font-medium text-gray-800">Wayja Wallet</span>
      );
    }

    const displayName = transaction.user_full_name && transaction.user_full_name.trim() !== ' '
      ? transaction.user_full_name
      : transaction.user_email || 'N/A';

    return (
      <div className="flex flex-col">
        <ClickableUserName userId={transaction.user_id} displayName={displayName} />
        {transaction.user_full_name && transaction.user_full_name.trim() !== ' ' && transaction.user_email && (
          <span className="text-xs text-gray-500">{transaction.user_email}</span>
        )}
      </div>
    );
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setDateFilter(value);
    if (value !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const formatDescription = (description: string | null) => {
    if (!description) return 'N/A';
    // Remove potential HTML tags for safety
    const sanitized = description.replace(/<[^>]*>?/gm, '');
    if (sanitized.length > 80) {
      return (
        <span title={sanitized}>
          {sanitized.substring(0, 80)}...
        </span>
      );
    }
    return sanitized;
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_admin_wallet_transactions', {
        p_page_size: 10000, // Large number to get all records
        p_page: 1,
        p_type: filterType !== 'all' ? filterType : null,
        p_status: filterStatus !== 'all' ? filterStatus : null,
        p_search: searchTerm || null,
        p_start_date: dateFilter !== 'all' && startDate ? new Date(startDate).toISOString() : null,
        p_end_date: dateFilter !== 'all' && endDate ? new Date(endDate).toISOString() : null,
        p_market_id: marketFilter && marketFilter.value !== 'all' ? marketFilter.value : null
      });

      if (error) throw error;
      
      // Create a new workbook and worksheet using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Transactions');

      // Add headers
      if (data && data.length > 0) {
        const headers = Object.keys(data[0]);
        worksheet.addRow(headers);
        
        // Add data rows
        data.forEach((row: Record<string, unknown>) => {
          worksheet.addRow(Object.values(row));
        });
        
        // Auto-fit columns
        worksheet.columns.forEach((column) => {
          if (column.header) {
            column.width = Math.max(column.header.length + 2, 15);
          }
        });
      }

      // Generate buffer and create download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error exporting data:', err);
      setError(err instanceof Error ? err.message : 'Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="py-8">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-3xl font-bold">Transaction History</h1>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search..."
              value={inputSearchTerm}
              onChange={(e) => setInputSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-48 rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
            <option value="bet_placement">Trade Placement</option>
            <option value="bet_win">Trade Win</option>
            <option value="bet_loss">Trade Loss</option>
            <option value="bet_cancelled">Trade Cancelled</option>
            <option value="payout">Payout</option>
            <option value="platform_liquidity_return">Collateral return</option>
            <option value="service_fee">Service Fee</option>
            <option value="service_fee_cancelled">Fee Refund</option>
            <option value="affiliate_commission">Affiliate Commission</option>
            <option value="creator_commission">Creator Commission</option>
          </select>
          
          {/* Status Filter */}
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          
          {/* Date Filter */}
          <select 
            value={dateFilter} 
            onChange={handleDateFilterChange}
            className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
            <option value="custom">Custom Range</option>
          </select>

          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              />
            </div>
          )}

          {/* Market Filter */}
          <Select
            value={marketFilter}
            onChange={(option) => setMarketFilter(option)}
            options={[{ value: 'all', label: 'All Markets' }, ...marketOptions.map((market) => ({ value: market.market_id, label: `${market.market_title} (${market.transaction_count})`}))]}
            isLoading={loadingMarkets}
            className="w-full sm:w-64"
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({
                ...base,
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#f97316'
                }
              }),
              option: (base, { isFocused, isSelected }) => ({
                ...base,
                backgroundColor: isSelected ? '#f97316' : isFocused ? '#fed7aa' : 'white',
                color: isSelected ? 'white' : 'black',
                '&:active': {
                  backgroundColor: '#fb923c'
                }
              }),
            }}
          />

          {/* Export Button */}
          <button 
            onClick={exportToExcel}
            disabled={exportLoading}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 ml-auto"
          >
            <Download className="w-5 h-5" />
            {exportLoading ? 'Exporting...' : 'Export All'}
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-600">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[24%]">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">Related</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openTransactionDetails(tx)}>
                      <td className="px-6 py-4 align-top whitespace-normal">{formatUserDisplay(tx)}</td>
                      <td className="px-6 py-4 align-top whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(tx.type)}
                          <span className="text-sm font-medium">{getTransactionDisplayType(tx.type)}</span>
                        </div>
                      </td>
                      <td className={`px-6 py-4 align-top whitespace-nowrap text-sm font-semibold ${isPositiveTransaction(tx.type, tx.amount) ? 'text-green-600' : 'text-red-600'}`}>
                        {isPositiveTransaction(tx.type, tx.amount) ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                      </td>
                      <td className="px-6 py-4 align-top whitespace-normal text-sm text-gray-600">{formatDescription(tx.description)}</td>
                      <td className="px-6 py-4 align-top whitespace-nowrap text-sm text-gray-500">{formatDate(tx.created_at)}</td>
                      <td className="px-6 py-4 align-top whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTransactionStatusColor(tx.status)}`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-top whitespace-normal">{getRelatedEntityInfo(tx)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setPage(1)} 
                  disabled={page === 1}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  First
                </button>
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))} 
                  disabled={page === 1}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Prev
                </button>
                {generatePageNumbers(page, totalPages).map((p, index) =>
                  typeof p === 'number' ? (
                    <button
                      key={index}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1 border rounded-md ${page === p ? 'bg-wayja-teal text-white' : ''}`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={index} className="px-3 py-1">...</span>
                  )
                )}
                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
                <button 
                  onClick={() => setPage(totalPages)} 
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10 flex justify-between items-center">
              <h3 className="text-xl font-bold">Transaction Details</h3>
              <button onClick={closeTransactionDetails} className="p-2 rounded-full hover:bg-gray-100">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">User</label>
                  <p className="text-lg font-semibold">{formatUserDisplay(selectedTransaction)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction ID</label>
                  <p className="text-sm text-gray-700 break-all">{selectedTransaction.id}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <div className="flex items-center gap-2 mt-1">
                    {getTransactionIcon(selectedTransaction.type)}
                    <p className="font-semibold">{getTransactionDisplayType(selectedTransaction.type)}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className={`mt-1 px-2 inline-flex text-sm leading-5 font-semibold rounded-full ${getTransactionStatusColor(selectedTransaction.status)}`}>
                    {selectedTransaction.status}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Amount</label>
                  <p className={`text-xl font-bold ${isPositiveTransaction(selectedTransaction.type, selectedTransaction.amount) ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositiveTransaction(selectedTransaction.type, selectedTransaction.amount) ? '+' : '-'}{formatCurrency(Math.abs(selectedTransaction.amount))}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Balance After</label>
                  <p className="text-lg font-semibold">{formatCurrency(selectedTransaction.balance_after)}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="mt-1 bg-gray-50 p-3 rounded-lg text-sm">{selectedTransaction.description || 'N/A'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Date Created</label>
                  <p className="text-sm">{formatDate(selectedTransaction.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date Completed</label>
                  <p className="text-sm">{selectedTransaction.completed_at ? formatDate(selectedTransaction.completed_at) : 'N/A'}</p>
                </div>
              </div>

              {selectedTransaction.related_entity_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Related Entity ID</label>
                    <p className="text-sm break-all">{selectedTransaction.related_entity_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Related Entity Type</label>
                    <p className="text-sm">{selectedTransaction.related_entity_type}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}